import { CacheData } from '../contexts/CacheContext';
import { EnhancedDashboardStats, CaseStatusData } from '../types/dashboard';

export interface CachedStatisticsOptions {
  timeRange?: '7d' | '30d' | '90d' | '1y';
  officeId?: string;
  clientId?: string;
}

export class CachedStatisticsService {
  private cache: CacheData;

  constructor(cache: CacheData) {
    this.cache = {
      offices: cache?.offices || [],
      clients: cache?.clients || [],
      events: cache?.events || [],
      legalFiles: cache?.legalFiles || [],
      systemPresetData: cache?.systemPresetData || null,
      roles: cache?.roles || [],
      usageStats: cache?.usageStats || null
    };
  }

  // Basic Statistics
  getBasicStats(): {
    totalClients: number;
    totalOffices: number;
    totalEvents: number;
    totalLegalFiles: number;
    activeEvents: number;
    completedEvents: number;
    upcomingEvents: number;
  } {
    try {
      console.log('📊 Getting basic stats from cache:', {
        offices: this.cache.offices?.length || 0,
        clients: this.cache.clients?.length || 0,
        events: this.cache.events?.length || 0,
        legalFiles: this.cache.legalFiles?.length || 0
      });

      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    const upcomingEvents = (this.cache.events || []).filter(event => {
      if (!event?.date) return false;
      const eventDate = new Date(event.date);
      return !isNaN(eventDate.getTime()) && eventDate > now && eventDate <= thirtyDaysFromNow;
    }).length;

    const completedEvents = (this.cache.events || []).filter(event => 
      event?.status === 3 // 3 - completed
    ).length;

    const activeEvents = (this.cache.events || []).filter(event => 
      event?.status === 2 || event?.status === 1 // 2 - in progress, 1 - upcoming
    ).length;

    return {
      totalClients: (this.cache.clients || []).length,
      totalOffices: (this.cache.offices || []).length,
      totalEvents: (this.cache.events || []).length,
      totalLegalFiles: (this.cache.legalFiles || []).length,
      activeEvents,
      completedEvents,
      upcomingEvents
    };
    } catch (error) {
      console.error('❌ Error in getBasicStats:', error);
      console.error('❌ Cache data:', this.cache);
      // Return default values to prevent complete failure
      return {
        totalClients: 0,
        totalOffices: 0,
        totalEvents: 0,
        totalLegalFiles: 0,
        activeEvents: 0,
        completedEvents: 0,
        upcomingEvents: 0
      };
    }
  }

  // Financial Statistics (based on legal files and events)
  getFinancialStats(): {
    totalRevenue: number;
    totalExpenses: number;
    netProfit: number;
    averageCaseValue: number;
    revenueByOffice: Array<{ officeId: string; officeName: string; revenue: number }>;
    monthlyRevenue: Array<{ month: string; revenue: number }>;
  } {
    // Calculate revenue from legal files (using totalIncome field)
    const totalRevenue = this.cache.legalFiles.reduce((sum, file) => 
      sum + (file.totalIncome || 0), 0
    );

    // Expenses: sum real totalExpenses fields (app ↔ web KPI parity, OW-276)
    const totalExpenses = this.cache.legalFiles.reduce((sum, file) =>
      sum + (file.totalExpenses || 0), 0
    );
    const netProfit = totalRevenue - totalExpenses;

    // Revenue by office
    const revenueByOffice = this.cache.offices.map(office => {
      const officeFiles = this.cache.legalFiles.filter(file => 
        file.officeID === office.id
      );
      const revenue = officeFiles.reduce((sum, file) => sum + (file.totalIncome || 0), 0);
      return {
        officeId: office.id,
        officeName: office.name,
        revenue
      };
    });

    // Monthly revenue (last 12 months)
    const monthlyRevenue = this.getMonthlyRevenue();

    return {
      totalRevenue,
      totalExpenses,
      netProfit,
      averageCaseValue: this.cache.legalFiles.length > 0 ? totalRevenue / this.cache.legalFiles.length : 0,
      revenueByOffice,
      monthlyRevenue
    };
  }

  // Client Analytics
  getClientAnalytics(): {
    clientGrowthRate: number;
    newClientsLast30Days: number;
    clientValueDistribution: Array<{ range: string; count: number }>;
    clientAcquisitionFunnel: Array<{ stage: string; count: number }>;
    topClients: Array<{ clientId: string; clientName: string; value: number; cases: number }>;
  } {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    const newClientsLast30Days = this.cache.clients.filter(client => {
      if (!client?.createdAt) return false;
      const clientDate = new Date(client.createdAt);
      return !isNaN(clientDate.getTime()) && clientDate >= thirtyDaysAgo;
    }).length;

    const clientGrowthRate = this.cache.clients.length > 0 ? 
      (newClientsLast30Days / this.cache.clients.length) * 100 : 0;

    // Client value distribution
    const clientValues = this.cache.clients.map(client => {
      const clientFiles = this.cache.legalFiles.filter(file => file.clientID === client.id);
      return clientFiles.reduce((sum, file) => sum + (file.totalIncome || 0), 0);
    });

    const clientValueDistribution = [
      { range: '0-10K', count: clientValues.filter(v => v < 10000).length },
      { range: '10K-50K', count: clientValues.filter(v => v >= 10000 && v < 50000).length },
      { range: '50K-100K', count: clientValues.filter(v => v >= 50000 && v < 100000).length },
      { range: '100K+', count: clientValues.filter(v => v >= 100000).length }
    ];

    // Top clients
    const topClients = this.cache.clients.map(client => {
      const clientFiles = this.cache.legalFiles.filter(file => file.clientID === client.id);
      const value = clientFiles.reduce((sum, file) => sum + (file.totalIncome || 0), 0);
      return {
        clientId: client.id,
        clientName: client.name,
        value,
        cases: clientFiles.length
      };
    }).sort((a, b) => b.value - a.value).slice(0, 10);

    return {
      clientGrowthRate,
      newClientsLast30Days,
      clientValueDistribution,
      clientAcquisitionFunnel: [
        { stage: 'Leads', count: this.cache.clients.length },
        { stage: 'Consultations', count: this.cache.events.filter(e => e.eventTypeID === '1').length },
        { stage: 'Active Cases', count: this.cache.legalFiles.filter(f => f.statusID === '1').length },
        { stage: 'Completed Cases', count: this.cache.legalFiles.filter(f => f.statusID === '3').length }
      ],
      topClients
    };
  }

  // Case Performance Analytics
  getCasePerformance(): {
    caseStatusDistribution: CaseStatusData[];
    winRate: number;
    averageCaseDuration: number;
    caseTypePerformance: Array<{ type: string; count: number; successRate: number }>;
    courtPerformance: Array<{ court: string; cases: number; successRate: number }>;
  } {
    const caseStatusDistribution: CaseStatusData[] = [
      { status: 'active', count: this.cache.legalFiles.filter(f => f.statusID === '1').length },
      { status: 'pending', count: this.cache.legalFiles.filter(f => f.statusID === '2').length },
      { status: 'completed', count: this.cache.legalFiles.filter(f => f.statusID === '3').length },
      { status: 'closed', count: this.cache.legalFiles.filter(f => f.statusID === '4').length }
    ];

    const completedCases = this.cache.legalFiles.filter(f => f.statusID === '3');
    const wonCases = completedCases.filter(f => f.caseStatusID === '1'); // Assuming 1 = won
    const winRate = completedCases.length > 0 ? (wonCases.length / completedCases.length) * 100 : 0;

    // Average case duration (simplified calculation)
    const casesWithDuration = this.cache.legalFiles.filter(f => f.createdAt && f.updatedAt);
    const averageCaseDuration = casesWithDuration.length > 0 ? 
      casesWithDuration.reduce((sum, file) => {
        const createdDate = new Date(file.createdAt);
        const updatedDate = new Date(file.updatedAt);
        if (isNaN(createdDate.getTime()) || isNaN(updatedDate.getTime())) {
          return sum; // Skip invalid dates
        }
        const duration = updatedDate.getTime() - createdDate.getTime();
        return sum + (duration / (1000 * 60 * 60 * 24)); // Convert to days
      }, 0) / casesWithDuration.length : 0;

    // Case type performance
    const caseTypeIds = this.cache.legalFiles.map(f => f.caseTypeID);
    const uniqueCaseTypes = Array.from(new Set(caseTypeIds));
    const caseTypePerformance = uniqueCaseTypes.map(type => {
      const typeCases = this.cache.legalFiles.filter(f => f.caseTypeID === type);
      const completedTypeCases = typeCases.filter(f => f.statusID === '3');
      const wonTypeCases = completedTypeCases.filter(f => f.caseStatusID === '1');
      const successRate = completedTypeCases.length > 0 ? (wonTypeCases.length / completedTypeCases.length) * 100 : 0;
      
      return {
        type: type || 'Unknown',
        count: typeCases.length,
        successRate
      };
    });

    return {
      caseStatusDistribution,
      winRate,
      averageCaseDuration,
      caseTypePerformance,
      courtPerformance: [] // Would need court data
    };
  }

  // Event Analytics
  getEventAnalytics(): {
    eventTypeDistribution: Array<{ type: string; count: number }>;
    eventsPerDay: Array<{ date: string; count: number }>;
    missedEvents: number;
    completedEvents: number;
    upcomingEventsThisWeek: number;
    eventEfficiency: number;
  } {
    const now = new Date();
    const oneWeekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    const eventTypeDistribution = [
      { type: 'client_meeting', count: this.cache.events.filter(e => e.eventTypeID === '1').length },
      { type: 'court_session', count: this.cache.events.filter(e => e.eventTypeID === '2').length },
      { type: 'governmental', count: this.cache.events.filter(e => e.eventTypeID === '3').length },
      { type: 'other', count: this.cache.events.filter(e => !['1', '2', '3'].includes(e.eventTypeID || '')).length }
    ];

    const completedEvents = this.cache.events.filter(e => e.status === 3).length;
    const missedEvents = this.cache.events.filter(e => {
      if (e.status === 5) return true;
      if (e.status === 1 && e?.date) {
        const eventDate = new Date(e.date);
        return !isNaN(eventDate.getTime()) && eventDate < now;
      }
      return false;
    }).length;

    const upcomingEventsThisWeek = this.cache.events.filter(e => {
      if (e.status === 1 && e?.date) {
        const eventDate = new Date(e.date);
        return !isNaN(eventDate.getTime()) && eventDate <= oneWeekFromNow;
      }
      return false;
    }).length;

    // Event efficiency (completed vs total)
    const eventEfficiency = this.cache.events.length > 0 ? 
      (completedEvents / this.cache.events.length) * 100 : 0;

    // Events per day (last 30 days)
    const eventsPerDay = this.getEventsPerDay();

    return {
      eventTypeDistribution,
      eventsPerDay,
      missedEvents,
      completedEvents,
      upcomingEventsThisWeek,
      eventEfficiency
    };
  }

  // Office Performance
  getOfficePerformance(): Array<{
    officeId: string;
    officeName: string;
    clients: number;
    cases: number;
    revenue: number;
    efficiency: number;
  }> {
    return this.cache.offices.map(office => {
      const officeClients = this.cache.clients.filter(c => c.rolesIDs?.includes(office.id));
      const officeFiles = this.cache.legalFiles.filter(f => f.officeID === office.id);
      const revenue = officeFiles.reduce((sum, file) => sum + (file.totalIncome || 0), 0);
      const completedCases = officeFiles.filter(f => f.statusID === '3').length;
      const efficiency = officeFiles.length > 0 ? (completedCases / officeFiles.length) * 100 : 0;

      return {
        officeId: office.id,
        officeName: office.name,
        clients: officeClients.length,
        cases: officeFiles.length,
        revenue,
        efficiency
      };
    });
  }

  // Generate comprehensive dashboard stats
  getEnhancedDashboardStats(options: CachedStatisticsOptions = {}): EnhancedDashboardStats {
    try {
      console.log('📊 Generating enhanced dashboard statistics from cached data');
      console.log('📊 Cache data summary:', {
        offices: this.cache.offices.length,
        clients: this.cache.clients.length,
        events: this.cache.events.length,
        legalFiles: this.cache.legalFiles.length,
        roles: this.cache.roles.length
      });

      const basicStats = this.getBasicStats();
      const financialStats = this.getFinancialStats();
      const clientAnalytics = this.getClientAnalytics();
      const casePerformance = this.getCasePerformance();
      const eventAnalytics = this.getEventAnalytics();
      const officePerformance = this.getOfficePerformance();

    console.log('📊 Generated statistics from cached data:', {
      basicStats,
      financialStats: {
        totalRevenue: financialStats.totalRevenue,
        netProfit: financialStats.netProfit,
        averageCaseValue: financialStats.averageCaseValue
      },
      clientAnalytics: {
        clientGrowthRate: clientAnalytics.clientGrowthRate,
        newClientsLast30Days: clientAnalytics.newClientsLast30Days
      },
      casePerformance: {
        winRate: casePerformance.winRate,
        averageCaseDuration: casePerformance.averageCaseDuration
      }
    });

    return {
      // Basic stats
      myClients: basicStats.totalClients,
      myOffices: basicStats.totalOffices,
      upcomingEvents: basicStats.upcomingEvents,
      legalFiles: basicStats.totalLegalFiles,
      recentActivity: basicStats.activeEvents,

      // Financial
      totalRevenue: financialStats.totalRevenue,
      totalExpenses: financialStats.totalExpenses,
      netProfit: financialStats.netProfit,
      revenueTrend: financialStats.monthlyRevenue.map(m => ({ 
        date: m.month, 
        revenue: m.revenue, 
        expenses: m.revenue * 0.3, 
        profit: m.revenue * 0.7, 
        cases: 0 // Would need case count per month
      })),
      expensesTrend: financialStats.monthlyRevenue.map(m => ({ 
        date: m.month, 
        value: m.revenue * 0.3
      })),
      profitTrend: financialStats.monthlyRevenue.map(m => ({ 
        date: m.month, 
        value: m.revenue * 0.7
      })),
      outstandingInvoices: 0, // Would need invoice data
      overdueInvoices: 0, // Would need invoice data
      paymentStatusDistribution: [],
      caseProfitability: [],

      // Client analytics
      clientGrowthRate: clientAnalytics.clientGrowthRate,
      newClientsLast30Days: clientAnalytics.newClientsLast30Days,
      clientAcquisitionFunnel: clientAnalytics.clientAcquisitionFunnel.map(f => ({ stage: f.stage, count: f.count })),
      clientValueDistribution: clientAnalytics.clientValueDistribution.map(v => ({ valueRange: v.range, count: v.count })),

      // Case performance
      caseStatusDistribution: casePerformance.caseStatusDistribution,
      caseTypePerformance: casePerformance.caseTypePerformance.map(c => ({ name: c.type, value: c.count })),
      winRate: casePerformance.winRate,
      averageCaseDuration: casePerformance.averageCaseDuration,
      courtPerformance: casePerformance.courtPerformance.map(c => ({ name: c.court, value: c.cases })),

      // Event analytics
      eventTypeDistribution: eventAnalytics.eventTypeDistribution.map(e => ({ name: e.type, value: e.count })),
      eventsPerDay: eventAnalytics.eventsPerDay.map(e => ({ date: e.date, value: e.count })),
      missedEvents: eventAnalytics.missedEvents,
      completedEvents: eventAnalytics.completedEvents,

      // Office performance
      officePerformanceComparison: officePerformance.map(o => ({
        officeId: o.officeId,
        name: o.officeName,
        clientsCount: o.clients,
        filesCount: o.cases,
        revenue: o.revenue
      }))
    };
    } catch (error) {
      console.error('❌ Error in getEnhancedDashboardStats:', error);
      console.error('❌ Cache data:', this.cache);
      throw error;
    }
  }

  // Helper methods
  private getMonthlyRevenue(): Array<{ month: string; revenue: number }> {
    const months: Array<{ month: string; revenue: number }> = [];
    const now = new Date();
    
    for (let i = 11; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
      
      // Calculate revenue for this month (simplified)
      const monthFiles = this.cache.legalFiles.filter(file => {
        if (!file?.createdAt) return false;
        const fileDate = new Date(file.createdAt);
        return !isNaN(fileDate.getTime()) && 
               fileDate.getMonth() === date.getMonth() && 
               fileDate.getFullYear() === date.getFullYear();
      });
      
      const revenue = monthFiles.reduce((sum, file) => sum + (file.totalIncome || 0), 0);
      months.push({ month: monthName, revenue });
    }
    
    return months;
  }

  private getEventsPerDay(): Array<{ date: string; count: number }> {
    const eventsPerDay: { [key: string]: number } = {};
    const now = new Date();
    
    // Initialize last 30 days
    for (let i = 29; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dateStr = date.toISOString().split('T')[0];
      eventsPerDay[dateStr] = 0;
    }
    
    // Count events per day
    this.cache.events.forEach(event => {
      if (event?.date) {
        const date = new Date(event.date);
        // Check if the date is valid
        if (!isNaN(date.getTime())) {
          const eventDate = date.toISOString().split('T')[0];
          if (eventsPerDay[eventDate] !== undefined) {
            eventsPerDay[eventDate]++;
          }
        } else {
          console.warn('⚠️ Invalid date found in event:', event);
        }
      }
    });
    
    return Object.entries(eventsPerDay).map(([date, count]) => ({ date, count }));
  }
}

// Factory function to create statistics service
export const createCachedStatisticsService = (cache: CacheData): CachedStatisticsService => {
  return new CachedStatisticsService(cache);
};
